import { useId, useState, type ReactNode } from 'react';
import { Box, Card, CardContent, Collapse, IconButton, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import theme from '../../theme/theme';

export type DetailCardProps = {
  /** Pre-rendered icon, sized at 20px, colored appropriately */
  icon: ReactNode;
  /** Background color of the icon badge (e.g. '#dbeafe') */
  iconBg: string;
  title: string;
  /** Optional subtitle rendered below the title */
  subtitle?: string;
  /** Rendered inline immediately after the title (e.g. a legend), before the
   *  flexible gap that pushes {@link headerExtra} to the right. */
  titleExtra?: ReactNode;
  children: ReactNode;
  /** Set to true when the card must fill remaining space in a flex: 1 wrapper */
  stretch?: boolean;
  /** Rendered on the right side of the card header (e.g. pagination controls) */
  headerExtra?: ReactNode;
  /** Colored top strip (CSS color). Adds a 3px accent bar at the top of the card. */
  strip?: string;
  /** Override body padding. Defaults to theme spacing 3 (24px). */
  bodyPadding?: string | number;
  /**
   * When true, the card body is collapsible: a chevron toggle is shown in the
   * header (far right) and the body is wrapped in an animated collapse region.
   * Opt-in — omitted everywhere the card is not meant to fold, so existing
   * callers are unaffected.
   */
  collapsible?: boolean;
  /** Initial expanded state when {@link collapsible}. Defaults to expanded. */
  defaultExpanded?: boolean;
};

/**
 * Section card with a colored icon badge header.
 *
 * Presentational when not collapsible. With `collapsible`, it owns a single
 * boolean of open/closed state (reset on remount — no persistence). The whole
 * header banner toggles the fold, with the chevron as the visual + accessible
 * (keyboard, aria-expanded) control. Interactive `headerExtra` controls (e.g.
 * pagination arrows) stop click propagation so they never toggle the card.
 */
export function DetailCard({
  icon,
  iconBg,
  title,
  subtitle,
  titleExtra,
  children,
  stretch = false,
  headerExtra,
  strip,
  bodyPadding,
  collapsible = false,
  defaultExpanded = true,
}: DetailCardProps) {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState(defaultExpanded);
  const contentId = useId();
  const toggle = () => setExpanded(e => !e);
  const body = <CardContent sx={{ p: bodyPadding ?? 3, flex: 1 }}>{children}</CardContent>;

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: theme.shadows[2],
        // `stretch` fills a fixed container height — incompatible with folding, since
        // a stretched card stays full-height and the collapse has no visible effect.
        // A collapsible card therefore always sizes to its content.
        ...(stretch && !collapsible && { height: '100%' }),
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.3s',
        '&:hover': { boxShadow: theme.shadows[4] },
        ...(strip && {
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: strip,
          },
        }),
      }}
    >
      {/* Card header — when collapsible, the whole banner toggles the fold (not just
          the chevron). Interactive controls inside the header (headerExtra) stop
          propagation so they never fold the card. The chevron stays the accessible
          control (keyboard + aria-expanded). */}
      <Box
        onClick={collapsible ? toggle : undefined}
        sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          ...(collapsible && { cursor: 'pointer', userSelect: 'none' }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: theme.avatar.md,
              height: theme.avatar.md,
              bgcolor: iconBg,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: '0 1 auto', minWidth: 0 }}>
            <Typography component="h2" variant="subtitle1" sx={{ color: theme.palette.text.body }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: theme.palette.text.label }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {titleExtra}
          {/* Flexible gap keeps headerExtra aligned to the right edge. */}
          <Box sx={{ flex: 1 }} />
          {headerExtra &&
            // Only collapsible cards need the propagation guard (and its wrapper);
            // non-collapsible cards render headerExtra bare, preserving their layout.
            (collapsible ? (
              <Box
                onClick={e => e.stopPropagation()}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {headerExtra}
              </Box>
            ) : (
              headerExtra
            ))}
          {collapsible && (
            <IconButton
              size="small"
              onClick={e => {
                // Header already toggles; stop the bubble so the chevron doesn't double-fire.
                e.stopPropagation();
                toggle();
              }}
              aria-expanded={expanded}
              aria-controls={contentId}
              aria-label={expanded ? t('collapsibleCard.collapse') : t('collapsibleCard.expand')}
              title={expanded ? t('collapsibleCard.collapse') : t('collapsibleCard.expand')}
              sx={{ flexShrink: 0, ml: 0.5 }}
            >
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transition: 'transform 0.2s',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}
              />
            </IconButton>
          )}
        </Box>
      </Box>

      {collapsible ? (
        <Collapse
          in={expanded}
          timeout="auto"
          // MUI sets the transition duration inline on the Collapse root, so the
          // reduced-motion override needs !important to also skip the height animation.
          sx={{
            '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms !important' },
          }}
        >
          <Box id={contentId}>{body}</Box>
        </Collapse>
      ) : (
        body
      )}
    </Card>
  );
}
